import React, { useRef, useEffect } from "react";
import {
  Chart,
  LineController,
  LineElement,
  PointElement,
  LinearScale,
  Title,
  Tooltip,
  Legend,
  Filler,
  ChartData,
  ChartOptions,
} from "chart.js";
import zoomPlugin from 'chartjs-plugin-zoom';

Chart.register(LineController, LineElement, PointElement, LinearScale, Title, Tooltip, Legend, Filler, zoomPlugin);

type ChartProps = {
  height?: number;
  width?: number;
  minChainage?: number;
  maxChainage?: number;
  sweepingData?: any[];
  tightData?: any[];
};

const generateRange = (start: number, end: number, increment: number) => {
  const result = []
  for (let index = start; index < end; index+=increment) {
    result.push(parseFloat((index + increment).toFixed(2)));
  }
  return result
}

const CurvatureGeometryMultiChart: React.FC<ChartProps> = ({
  height = 230,
  width = 600,
  minChainage = 0,
  maxChainage = 66,
  sweepingData,
  tightData
}) => {
  
  
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const chartInstance = useRef<Chart | null>(null);
  useEffect(() => {
    
    const chainageLabels = generateRange(minChainage, maxChainage, 0.01)
    
    const sweepingChartData = chainageLabels.map((item)=>{
      const index = sweepingData?.findIndex((data)=>{
        return (item >= data.from_ch_km && item <= data.to_ch_km)
      })
      if (index && index !== -1) {
        return sweepingData?.[index]?.min_radius_m
      }
      return 0
    })
    const tightChartData = chainageLabels.map((item)=>{
      const index = tightData?.findIndex((data)=>{
        return (item >= data.from_ch_km && item <= data.to_ch_km)
      })
      if (index && index !== -1) {
        return tightData?.[index]?.min_radius_m
      }
      return 0
    })
    
    const options: ChartOptions<"line"> = {
      responsive: true,
      plugins: {
        legend: { display: true },
        title: {
          display: true,
          text: "Radius vs Chainage (Planar/ Cursor/ Tight Bend Zone)",
        },
        tooltip: {
          callbacks: {
            label: (context) => {
              const chainage = context.parsed.x ?? 0; // fallback to 0 if null/undefined
              const radius = context.parsed.y ?? 0;
              return `Chainage: ${chainage} km, Radius: ${radius.toFixed(0)} m`;
            },
          },
        },
        zoom: {
          zoom: {
            wheel: {
              enabled: true,
            },
            pinch: {
              enabled: true
            },
            mode: 'x',
          },
          pan: {
            enabled: true,
            mode: 'x'
          }
        }
      },
      scales: {
        x: {
          type: "linear",
          min: minChainage,
          max: maxChainage,
          title: { display: true, text: "Chainage (km)" },
          ticks: { stepSize: 0.1 },
        },
        y: {
          min: 0,
          max: 1000,
          title: { display: true, text: "Radius (m)" },
          ticks: { stepSize: 10 },
        },
      },
    };
    const data: ChartData<"line"> = {
      labels: chainageLabels,
      datasets: [
        {
          label: "Sweeping Curve",
          data: sweepingChartData,
          borderColor: "rgb(208, 197, 48)",
          borderWidth: 2,
          pointRadius: 0,
          fill: false,
          tension: 0.2,
        },
        {
          label: "Tight Curve",
          data: tightChartData,
          borderColor: "rgb(219, 30, 13)",
          borderWidth: 2,
          pointRadius: 0,
          fill: false,
          tension: 0.2,
        },
      ],
    };
    
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    
    if (canvasRef.current) {
      if (chartInstance.current) {
        chartInstance.current.destroy();
        chartInstance.current = null
      }
      chartInstance.current = new Chart(ctx, {
        type: "line",
        data,
        options,
      });
    }
    return () => {
      chartInstance.current?.destroy();
      chartInstance.current = null
    };
  }, [maxChainage, minChainage, sweepingData, tightData]);

  return (
    <canvas ref={canvasRef} width={width} height={height} />
  );
};

export default CurvatureGeometryMultiChart;